import type { Dayjs } from 'dayjs';

import { opTime } from 'utils/dates';

/**
 * Convert local time to UTC based on a specific timezone
 * @param date - Date to convert (dayjs, Date, string)
 * @param tz - Timezone (e.g., 'America/New_York', 'Asia/Seoul')
 * @returns Dayjs object converted to UTC
 *
 * @example
 * // Convert user-selected date to UTC based on location timezone
 * const utcDate = toUTCByTimezone(selectedDate, location.timezone);
 * const formatted = utcDate.format('YYYY-MM-DDTHH:mm:ss[Z]');
 */
export const toUTCByTimezone = (
  date: Dayjs | Date | string | null | undefined,
  tz: string | null | undefined,
): Dayjs | null => {
  if (!date) return null;

  const targetTz = tz || 'UTC';

  const dateStr = opTime(date).format('YYYY-MM-DD HH:mm:ss');
  return opTime.tz(dateStr, targetTz).utc();
};
/**
 * Convert UTC time to local time based on a specific timezone.
 * Falls back to the browser's/user's timezone if no tz is provided.
 * @param date - UTC date (dayjs, Date, string)
 * @param tz - Timezone (e.g., 'America/New_York', 'Asia/Seoul')
 * @returns Dayjs object converted to the specified timezone's local time
 */
export const toLocalByTimezone = (
  date: Dayjs | Date | string | null | undefined,
  tz: string | null | undefined,
): Dayjs | null => {
  if (!date) return null;

  const targetTz = tz || opTime.tz.guess();

  return opTime.utc(date).tz(targetTz);
};
